import type {
  APIKey,
  APIKeyApi,
  AlarmLogApi,
  FrameApi,
  FrameData,
  LabelingApi,
  Modeling,
  ModelingApi,
  ObjectClass,
  Project,
  ProjectApi,
  StatisticApi,
  TaskApi,
  TrackApi,
  UserApi,
  UserGroupApi,
} from '@/type';

export const USER_KEYS = {
  getList: (filter?: Partial<UserApi.GetList.Request>) =>
    filter ? (['userApi.getList', filter] as const) : ['userApi.getList'],
  getCount: (filter?: Partial<UserApi.GetCount.Request>) =>
    filter ? (['userApi.getCount', filter] as const) : ['userApi.getCount'],
};

export const USER_GROUP_KEYS = {
  getList: (filter?: Partial<UserGroupApi.GetList.Request>) =>
    filter
      ? (['userGroupApi.getList', filter] as const)
      : (['userGroupApi.getList'] as const),
};

export const PROJECT_KEYS = {
  getDetail: (id?: Project['id']) =>
    id
      ? (['projectApi.getDetail', id] as const)
      : (['projectApi.getDetail'] as const),
  getList: (filter?: Partial<ProjectApi.GetList.Request>) =>
    filter
      ? (['projectApi.getList', filter] as const)
      : (['projectApi.getList'] as const),
  getCount: (filter?: Partial<ProjectApi.GetList.Request>) =>
    filter
      ? (['projectApi.getCount', filter] as const)
      : (['projectApi.getCount'] as const),
};

export const TASK_KEYS = {
  getDetail: (filter?: Partial<TaskApi.GetDetail.Request>) =>
    filter
      ? (['taskApi.getDetail', filter] as const)
      : (['taskApi.getDetail'] as const),
  getList: (filter?: Partial<TaskApi.GetList.Request>) =>
    filter
      ? (['taskApi.getList', filter] as const)
      : (['taskApi.getList'] as const),
  getCount: (filter?: TaskApi.GetCount.Request) =>
    filter
      ? (['taskApi.getCount', filter] as const)
      : (['taskApi.getCount'] as const),
  getDistributePreview: (param?: TaskApi.GetDistributePreview.Request) =>
    param
      ? (['taskApi.getDistributePreview', param] as const)
      : (['taskApi.getDistributePreview'] as const),
};

export const FRAME_KEYS = {
  getDetail: (param?: {
    taskID: FrameData['taskID'];
    frameSeq: FrameData['frameSeq'];
  }) => {
    return param
      ? ['frameApi.getDetail', param]
      : (['frameApi.getDetail'] as const);
  },
  getList: (filter?: Partial<FrameApi.GetList.Request>) =>
    filter
      ? (['frameApi.getList', filter] as const)
      : (['frameApi.getList'] as const),
  getCount: (
    param?: Partial<{
      workerID: string[] | null;
      reviewerID: string[] | null;
    }> & {
      workState?: FrameData['workState'][];
    } & Pick<FrameData, 'taskID'>,
  ) =>
    param
      ? (['frameApi.getCount', param] as const)
      : (['frameApi.getCount'] as const),
  getNeighbor: (param?: FrameApi.GetNeighbor.Request) =>
    param
      ? (['frameApi.getNeighbor', param] as const)
      : (['frameApi.getNeighbor'] as const),
  getHistoryList: (param?: FrameApi.GetHistoryList.Request) =>
    param
      ? (['frameApi.getHistoryList', param] as const)
      : (['frameApi.getHistoryList'] as const),
};

export const MIN_IO = {
  dir: (path: string) => ['readDir', path] as const,
};

export const MODELING_KEYS = {
  getCount: (param?: Partial<ModelingApi.GetCount.Request>) =>
    param
      ? (['modelingApi.getCount', param] as const)
      : (['modelingApi.getCount'] as const),
  getList: (filter?: Partial<ModelingApi.GetList.Request>) =>
    filter
      ? (['modelingApi.getList', filter] as const)
      : (['modelingApi.getList'] as const),
  getDetail: (id: Modeling['id']) => ['modelingApi.getDetail', id] as const,
  getPerformance: (id: Modeling['id']) =>
    ['modelingApi.getPerformance', id] as const,
};

export const PRE_ANNOTATION_KEYS = {
  currentCount: ['preAnnotation.currentCount'] as const,
  totalCount: ['preAnnotation.totalCount'] as const,
};

export const STATISTIC_KEYS = {
  object: (params?: StatisticApi.GetObject.Request) =>
    params
      ? (['statisticApi.object', JSON.stringify(params)] as const)
      : (['statisticApi.object'] as const),
  state: (params?: StatisticApi.GetState.Request) =>
    params
      ? (['statisticApi.state', JSON.stringify(params)] as const)
      : (['statisticApi.state'] as const),
  metric: (params?: StatisticApi.GetMetric.Request) =>
    params
      ? (['statisticApi.metric', JSON.stringify(params)] as const)
      : (['statisticApi.metric'] as const),
};

export const ALARM_LOG_KEYS = {
  getList: (param?: AlarmLogApi.GetList.Request) =>
    ['alarmLogApi.getList', JSON.stringify(param ?? {})] as const,
};

export const LABELLING_KEYS = {
  getObjectClassList: (param?: LabelingApi.GetObjectClassList.Request) =>
    param
      ? (['labelingApi.getObjectClassList', JSON.stringify(param)] as const)
      : (['labelingApi.getObjectClassList'] as const),
  getObjectClass: (pObjectClassID: ObjectClass['id']) =>
    ['labelingApi.getObjectClass', pObjectClassID] as const,
  getObjectClassCount: () => ['labelingApi.getObjectClassCount'] as const,
  createObjectClass: (params: LabelingApi.CreateObjectClass.Request) =>
    ['labelingApi.createObjectClass', JSON.stringify(params)] as const,
  updateObjectClass: (pObjectClassID: ObjectClass['id']) =>
    ['labelingApi.updateObjectClass', pObjectClassID] as const,
};

export const ICON_KEYS = {
  getObjectClassIconList: (
    params?: LabelingApi.GetExtendClassIconList.Request,
  ) =>
    params
      ? (['iconApi.getObjectClassIconList', JSON.stringify(params)] as const)
      : (['iconApi.getObjectClassIconList'] as const),
};

export const API_KEY_KEYS = {
  get: (id?: APIKey['id']) =>
    id ? (['apiKeyApi.get', id] as const) : (['apiKeyApi.get'] as const),
  getCount: (param?: APIKeyApi.GetCount.Request) =>
    param
      ? (['apiKeyApi.getCount', JSON.stringify(param)] as const)
      : (['apiKeyApi.getCount'] as const),
  getList: (param?: APIKeyApi.GetList.Request) =>
    param
      ? (['apiKeyApi.getList', JSON.stringify(param)] as const)
      : (['apiKeyApi.getList'] as const),
};

export const TENANT_KEYS = {
  getUsage: () => ['tenantApi.getUsage'] as const,
};

export const TRACK_KEYS = {
  getTrackByFrameSeq: (param?: TrackApi.GetTrackByFrameSeq.Request) =>
    param
      ? (['trackApi.getTrackByFrameSeq', JSON.stringify(param)] as const)
      : (['trackApi.getTrackByFrameSeq'] as const),
};
